import { Link, useLoaderData } from "react-router-dom";
import { NEXT_TONE_CLASS } from "../data/status";
import { Panel, PanelHead } from "../components/Panel";
import { PriorityBadge } from "../components/Priority";
import { StatusLabel } from "../components/StatusDot";
import { OPEN_STATUSES } from "../data/status";
import type { MyWorkData } from "../data/loaders";

export function MyWork() {
  // The loader fetched every request I am on (technical owner or account lead); split by open vs closed.
  const { memberId, requests: all } = useLoaderData() as MyWorkData;
  const mine = all.filter((r) => OPEN_STATUSES.includes(r.status));
  const closed = all.filter((r) => !OPEN_STATUSES.includes(r.status));

  return (
    <div className="mx-auto max-w-[1000px] px-6 py-8">
      <h1 className="mb-5 text-display-xs font-semibold text-gray-900">My work</h1>

      {memberId === null ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg)] p-12 text-center">
          <h3 className="text-md font-medium text-gray-900">Your account isn't linked to a team member yet</h3>
          <p className="mt-1 text-sm text-[var(--text-2)]">An admin can add your email to your entry in Settings › Team; your requests will show here after that.</p>
        </div>
      ) : mine.length === 0 ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg)] p-12 text-center">
          <h3 className="text-md font-medium text-gray-900">You’re clear</h3>
          <p className="mt-1 text-sm text-[var(--text-2)]">Nothing open has you as technical owner or account lead right now.</p>
        </div>
      ) : (
        <Panel labelledBy="mine-h">
          <PanelHead id="mine-h" title="Assigned to me" count={mine.length} />
          {mine.map((r) => (
            <Link
              key={r.id}
              to={`/requests/${r.id}`}
              className="grid grid-cols-[26px_minmax(0,1fr)_150px_100px] items-center gap-3 border-b border-[var(--border)] px-4 py-2.5 text-sm transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring"
            >
              <PriorityBadge priority={r.priority} />
              <span className="truncate">{r.title}</span>
              <StatusLabel status={r.status} />
              <span className={`text-right text-xs ${NEXT_TONE_CLASS[r.nextTone]}`}>{r.nextLabel}</span>
            </Link>
          ))}
        </Panel>
      )}

      {closed.length > 0 && (
        <div className="mt-5">
          <Panel labelledBy="closed-h">
            <PanelHead id="closed-h" title="Recently closed by you" count={closed.length} />
            {closed.map((r) => (
              <Link
                key={r.id}
                to={`/requests/${r.id}`}
                className="grid grid-cols-[26px_minmax(0,1fr)_150px] items-center gap-3 border-b border-[var(--border)] px-4 py-2.5 text-sm text-[var(--text-2)] transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring"
              >
                <PriorityBadge priority={r.priority} />
                <span className="truncate">{r.title}</span>
                <StatusLabel status={r.status} />
              </Link>
            ))}
          </Panel>
        </div>
      )}
    </div>
  );
}
